import { AlertCircle, ArrowLeft, CheckCircle, ChevronRight, FileText } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { DocumentChecklistItem } from '@/components/documents/DocumentChecklistItem';
import { toast } from '@/lib/toast';
import { useDocumentStore } from '@/store/documentStore';

import type { DocumentItem } from '@/types/document.types';

function isDocumentSatisfied(item: DocumentItem): boolean {
  return item.status === 'uploaded' || item.status === 'verified';
}

export function DocumentChecklistPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const categories = useDocumentStore((state) => state.categories);
  const isLoading = useDocumentStore((state) => state.isLoading);
  const isUploading = useDocumentStore((state) => state.isUploading);
  const error = useDocumentStore((state) => state.error);
  const loadChecklist = useDocumentStore((state) => state.loadChecklist);
  const uploadDocument = useDocumentStore((state) => state.uploadDocument);
  const reuseDocument = useDocumentStore((state) => state.reuseDocument);
  const removeDocument = useDocumentStore((state) => state.removeDocument);
  const getProgress = useDocumentStore((state) => state.getProgress);
  const isReusing = useDocumentStore((state) => state.isReusing);

  const routeStateApplicationId = (location.state as { applicationId?: string } | null)?.applicationId;
  const applicationId = searchParams.get('applicationId') ?? routeStateApplicationId;
  const dashboardUrl = applicationId ? `/dashboard?applicationId=${encodeURIComponent(applicationId)}` : '/dashboard';

  useEffect(() => {
    if (!applicationId) {
      void navigate('/dashboard', { replace: true });
      return;
    }

    void loadChecklist(applicationId);
  }, [loadChecklist, navigate, applicationId]);

  const { completed, total } = getProgress();
  const isAllRequiredComplete = total > 0 && completed >= total;
  const progressPercent = total > 0 ? Math.round((completed / total) * 100) : 0;

  // Notify when all required documents are complete
  useEffect(() => {
    if (isAllRequiredComplete && !isUploading && total > 0) {
      toast.success(t('documents.allComplete'));
    }
  }, [isAllRequiredComplete, isUploading, total, t]);

  const handleUpload = useCallback(
    async (item: DocumentItem, file: File): Promise<boolean> => {
      const success = await uploadDocument(item, file);
      if (success) {
        toast.success(t('documents.toasts.uploadSuccess', { name: item.name }));
      } else {
        const uploadErr = useDocumentStore.getState().uploadError;
        if (uploadErr) {
          // Error is shown inline in UploadProgress — no toast needed
        } else {
          toast.error(t('documents.toasts.uploadFailed'));
        }
      }
      return success;
    },
    [uploadDocument, t],
  );

  const handleRemove = useCallback(
    async (documentId: string) => {
      const success = await removeDocument(documentId);
      if (success) {
        toast.success(t('documents.toasts.removeSuccess'));
      } else {
        toast.error(t('documents.toasts.removeFailed'));
      }
    },
    [removeDocument, t],
  );

  const handleReuse = useCallback(
    async (item: DocumentItem) => {
      const success = await reuseDocument(item);
      if (success) {
        toast.success(t('documents.toasts.reuseSuccess', { name: item.name }));
      } else {
        toast.error(t('documents.toasts.reuseFailed'));
      }
    },
    [reuseDocument, t],
  );

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent' />
      </div>
    );
  }

  return (
    <div className='max-w-3xl mx-auto p-4 md:p-8'>
      {/* Header */}
      <div className='mb-8'>
        <button
          type='button'
          onClick={() => void navigate(dashboardUrl)}
          className='flex items-center gap-1 text-sm text-gray-500 hover:text-primary transition mb-4'>
          <ArrowLeft size={16} />
          {t('documents.backToDashboard')}
        </button>
        <h1 className='text-3xl font-bold text-gray-900'>{t('documents.title')}</h1>
        <p className='text-gray-500 mt-2'>{t('documents.subtitle')}</p>
      </div>

      {/* Progress Bar */}
      <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8'>
        <div className='flex items-center justify-between mb-3'>
          <div className='flex items-center gap-3'>
            <div className={`p-2.5 rounded-full ${isAllRequiredComplete ? 'bg-green-100' : 'bg-blue-100'}`}>
              {isAllRequiredComplete ? (
                <CheckCircle className='h-5 w-5 text-green-600' />
              ) : (
                <FileText className='h-5 w-5 text-primary' />
              )}
            </div>
            <div>
              <h2 className='text-lg font-bold text-gray-900'>{t('documents.progress.title')}</h2>
              <p className='text-sm text-gray-500'>{t('documents.progress.status', { completed, total })}</p>
            </div>
          </div>
          <span className='text-2xl font-bold text-gray-900'>{progressPercent}%</span>
        </div>
        <div className='w-full bg-gray-200 rounded-full h-2.5'>
          <div
            className={`h-2.5 rounded-full transition-all duration-500 ${isAllRequiredComplete ? 'bg-green-500' : 'bg-primary'}`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3'>
          <AlertCircle className='h-5 w-5 text-red-500 shrink-0' />
          <p className='text-sm text-red-700'>{error}</p>
        </div>
      )}

      {/* Document Categories */}
      <div className='space-y-6'>
        {categories.map((category) => {
          const catCompleted = category.items.filter(isDocumentSatisfied).length;
          const catTotal = category.items.length;

          return (
            <div key={category.id} className='bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden'>
              {/* Category Header */}
              <div className='px-6 py-4 border-b border-gray-100 bg-gray-50/50'>
                <div className='flex items-center justify-between'>
                  <div>
                    <h3 className='font-bold text-gray-900'>{category.name}</h3>
                    <p className='text-sm text-gray-500 mt-0.5'>{category.description}</p>
                  </div>
                  <span className='text-xs font-medium text-gray-500 bg-white px-2.5 py-1 rounded-full border border-gray-200'>
                    {catCompleted}/{catTotal}
                  </span>
                </div>
              </div>

              {/* Items */}
              <div className='divide-y divide-gray-100'>
                {category.items.map((item) => {
                  const { documentId } = item;
                  return (
                    <DocumentChecklistItem
                      key={item.type}
                      item={item}
                      isUploading={isUploading === item.type}
                      isReusing={isReusing === item.type}
                      onUpload={(file) => handleUpload(item, file)}
                      onReuse={item.reusableDocumentId ? () => void handleReuse(item) : undefined}
                      onRemove={documentId ? () => void handleRemove(documentId) : undefined}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Action */}
      <div className='flex justify-end mt-8'>
        <button
          type='button'
          onClick={() => void navigate(dashboardUrl)}
          className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold text-white shadow transition ${
            isAllRequiredComplete ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 hover:bg-gray-500'
          }`}>
          {isAllRequiredComplete ? t('documents.completeAndReturn') : t('documents.returnToDashboard')}
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
}
